import { type ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { HallidayPayments } from "@halliday-sdk/payments";
import type { HallidayPaymentsConfig } from "@halliday-sdk/payments";
import { devWarn } from "../modules/devWarn";

import { HallidayContext, type ConfigAddrFlags, type HallidayContextValue } from "./context";
import { diffConfig } from "./diffConfig";
import type { HallidayPaymentsProviderProps, ProviderConfig } from "./types";

// Keys that mark a props object as carrying declarative config (used by the BYO-vs-config guard).
// Anchored via `as const satisfies …[]` so a bogus/removed key fails typecheck; a `satisfies` on the
// `new Set([...])` arg would NOT constrain members, so anchor the array first, then seed the Set from it.
const CONFIG_PROP_KEY_LIST = [
    "apiKey",
    "sandbox",
    "owner",
    "onConnectWallet",
    "feeSponsorship",
    "deposit",
    "withdrawal",
    "customStyles",
    "fontName",
    "headerTitle",
    "targetElementId",
    "autoPreload",
    "instanceId",
    "dangerousOverrides",
] as const satisfies readonly (keyof HallidayPaymentsConfig)[];
const CONFIG_PROP_KEYS = new Set<string>(CONFIG_PROP_KEY_LIST);

/**
 * Provides a single `HallidayPayments` instance to the subtree. Two mutually-exclusive modes:
 *
 * - **Owned (config props):** the Provider constructs the instance once (with `autoPreload:false`), warms it
 *   on mount, pushes reactive prop changes through `diffConfig`→`updateConfig`, and `destroy()`s it on
 *   unmount. The host wallet-connect hook rides a stable proxy (`connectRef`) so it stays current without a
 *   re-wire and without surviving the (function-excluding) diff.
 * - **BYO (`instance` prop):** the Provider uses the caller's instance and never preloads or destroys it —
 *   the caller owns its lifecycle.
 *
 * `<HallidayEmbed>` child effects run before this Provider's warm effect, so registering an EMBED target via
 * `embed()` happens before warming → the iframe is born in EMBED mode with no MODAL→EMBED reload.
 */
export const HallidayPaymentsProvider = (props: HallidayPaymentsProviderProps): ReactElement => {
    const byo = "instance" in props ? props.instance : undefined;

    // BYO-vs-config guard: supplying both is ambiguous. Throw early (before hooks) so it fails fast and
    // deterministically; the consumer must pick one mode.
    if (byo) {
        const hasConfigProps = Object.keys(props).some(
            (key) => key !== "instance" && key !== "children" && CONFIG_PROP_KEYS.has(key),
        );
        if (hasConfigProps) {
            throw new Error(
                "HallidayPaymentsProvider: pass either a bring-your-own `instance` OR config props, not both.",
            );
        }
    }

    // The declarative config slice (everything but children/instance). Undefined in BYO mode.
    const config: ProviderConfig | undefined = byo
        ? undefined
        : (() => {
              const {
                  children: _children,
                  instance: _instance,
                  ...rest
              } = props as ProviderConfig & {
                  children?: unknown;
                  instance?: unknown;
              };
              return rest as ProviderConfig;
          })();

    const generatedId = useId();
    const instanceId = config?.instanceId ?? generatedId;

    // Latest-wins host connect hook + which destination addresses came from declarative props (read by the
    // hook's double-set dev-warn). Both are "latest value" refs consumed lazily at event time — the connect
    // proxy `() => connectRef.current?.()` and `useHallidayPayments().updateWallets`.
    //
    // Seed once from the initial config in the useRef initializer (a plain render-time READ, which is safe),
    // then re-sync after every COMMITTED render via the effect below. We deliberately do NOT assign these
    // refs in the render body: that also runs on speculative/discarded concurrent renders (transitions,
    // Suspense, offscreen), which are never rolled back and could leave a ref holding an uncommitted value.
    const connectRef = useRef<(() => void) | undefined>(config?.onConnectWallet);
    const generateGrantRef = useRef<((payload: string) => Promise<string>) | undefined>(
        config?.feeSponsorship?.generateGrant,
    );
    const configAddrRef = useRef<ConfigAddrFlags>({
        deposit: config?.deposit?.destinationAddress != null,
        withdrawal: config?.withdrawal?.destinationAddress != null,
    });

    // No dependency array: these track values that can change on any render, so we want a re-sync on every
    // commit rather than conditional skipping. Plain useEffect (not useLayoutEffect) keeps this SSR-clean —
    // nothing reads these refs synchronously during commit, only in event-time callbacks.
    useEffect(() => {
        connectRef.current = config?.onConnectWallet;
        generateGrantRef.current = config?.feeSponsorship?.generateGrant;
        configAddrRef.current = {
            deposit: config?.deposit?.destinationAddress != null,
            withdrawal: config?.withdrawal?.destinationAddress != null,
        };
    });

    // Construct the owned instance once. `autoPreload:false` hands warming to the mount effect (which runs
    // after children mount), so an EMBED target registered by <HallidayEmbed> is in place before the warm.
    const [core] = useState<HallidayPayments>(() => {
        if (byo) return byo;
        return new HallidayPayments({
            ...(config as ProviderConfig),
            instanceId,
            autoPreload: false,
            // Install the connect wrapper ONLY when the host actually passed a handler, so the constructor
            // bakes an honest hasConnect. Identity swaps still ride connectRef (no re-wire); the presence
            // toggle (below) handles the prop appearing/disappearing after mount.
            ...(config?.onConnectWallet ? { onConnectWallet: () => connectRef.current?.() } : {}),
            // Same stable-ref wiring as onConnectWallet, but fee sponsorship is a nested group whose signer is
            // async and RETURNS a value. Rebuild the group (overriding the raw `...config` spread above) with a
            // stable signer proxy so an identity swap rides `generateGrantRef` (no re-wire), keeping the
            // serializable `profileName` as-is. The `?? Promise.reject(...)` fallback preserves the
            // `(payload: string) => Promise<string>` return type — `generateGrantRef.current?.(payload)` alone is
            // `Promise<string> | undefined`, which is not assignable under exactOptionalPropertyTypes/strict.
            ...(config?.feeSponsorship
                ? {
                      feeSponsorship: {
                          profileName: config.feeSponsorship.profileName,
                          generateGrant: (payload: string): Promise<string> =>
                              generateGrantRef.current?.(payload) ??
                              Promise.reject(new Error("No generateFeeSponsorshipGrant provided")),
                      },
                  }
                : {}),
        });
    });

    // EMBED mount-point registration (singleton per Provider). Returns a cleanup that restores MODAL.
    const embedIdRef = useRef<string | null>(null);
    const embed = useCallback<HallidayContextValue["embed"]>(
        (id: string) => {
            if (embedIdRef.current !== null && embedIdRef.current !== id) {
                devWarn(
                    "[HallidayPayments] Multiple <HallidayEmbed> mount points under one Provider are not " +
                        "supported — ignoring the extra one. Render a single <HallidayEmbed>.",
                );
                return;
            }
            embedIdRef.current = id;
            core.updateConfig({ targetElementId: id });
            return () => {
                if (embedIdRef.current !== id) return;
                embedIdRef.current = null;
                // The client's embed container is unmounting (their modal closed): hide the widget so it
                // doesn't linger as a 0x0/floating iframe over now-dead positioning. close() → hideWidget flips
                // isOpen:false and fires "close" while keeping the warm iframe ALIVE for the next open. It is a
                // HIDE, never a destroy() (destroy would throw away the warm iframe this pattern relies on).
                core.close();
                // Clear the EMBED target → back to MODAL. Use null (the mergeParams delete-signal) so
                // the key is removed rather than stored as undefined. Built via Record so the explicit
                // null passes exactOptionalPropertyTypes.
                const reset: Record<string, unknown> = { targetElementId: null };
                core.updateConfig(reset as Partial<HallidayPaymentsConfig>);
            };
        },
        [core],
    );

    const ctxValue = useMemo<HallidayContextValue>(() => ({ core, configAddrRef, embed }), [core, embed]);

    // Reactive props → updateConfig (owned only). The serialized config is the effect's trigger; on mount we
    // just record the baseline (the constructor already applied it), then diff prev→next on each change.
    const prevConfigRef = useRef<ProviderConfig | null>(null);
    const configKey = byo || !config ? null : JSON.stringify(config);
    useEffect(() => {
        if (byo || !config) return;
        const prev = prevConfigRef.current;
        if (prev) {
            const diff = diffConfig(prev, config);
            if (Object.keys(diff).length > 0) core.updateConfig(diff);
        }
        prevConfigRef.current = config;
    }, [configKey, core]);

    // Presence toggle for the (function-excluded) onConnectWallet prop. diffConfig never sees the prop
    // appear/disappear, so push the toggle through updateConfig; identity swaps stay on connectRef (no re-wire).
    // Gate on the boolean, not the closure identity (a new closure every render would fire this constantly),
    // and skip on mount (the constructor already applied the initial presence).
    const hasConnectProp = !byo && !!config?.onConnectWallet;
    const prevHasConnectRef = useRef(hasConnectProp);
    useEffect(() => {
        if (byo) return;
        if (prevHasConnectRef.current === hasConnectProp) return;
        prevHasConnectRef.current = hasConnectProp;
        // Record<> + cast so the explicit `undefined` clears under exactOptionalPropertyTypes (mirror `embed`).
        const patch: Record<string, unknown> = {
            onConnectWallet: hasConnectProp ? () => connectRef.current?.() : undefined,
        };
        core.updateConfig(patch as Partial<HallidayPaymentsConfig>);
    }, [hasConnectProp, byo, core]);

    // feeSponsorship carries a function (generateGrant) → diffConfig can't see the signer appear/disappear,
    // exactly like onConnectWallet. Push the signer presence toggle through updateConfig; identity swaps stay
    // on generateGrantRef (no re-wire). The serializable `profileName` rides diffConfig (added/renamed/cleared
    // there like any group sub-key), so this effect only toggles the signer. Gate on the boolean; skip on mount.
    // The `{ generateGrant: undefined }` clear survives normalizeConfig's `!= null` group gate (the object is
    // non-null; the key-present check inside carries the `undefined` through).
    const hasFeeSponsorshipProp = !byo && !!config?.feeSponsorship;
    const prevHasFeeSponsorshipRef = useRef(hasFeeSponsorshipProp);
    useEffect(() => {
        if (byo) return;
        if (prevHasFeeSponsorshipRef.current === hasFeeSponsorshipProp) return;
        prevHasFeeSponsorshipRef.current = hasFeeSponsorshipProp;
        const patch: Record<string, unknown> = {
            feeSponsorship: {
                generateGrant: hasFeeSponsorshipProp
                    ? (payload: string): Promise<string> =>
                          generateGrantRef.current?.(payload) ??
                          Promise.reject(new Error("No feeSponsorship.generateGrant provided"))
                    : undefined,
            },
        };
        core.updateConfig(patch as Partial<HallidayPaymentsConfig>);
    }, [hasFeeSponsorshipProp, byo, core]);

    // Warm on mount, tear down on unmount — owned instances only (BYO lifecycle belongs to the caller).
    useEffect(() => {
        if (byo) return;
        core.preload();
        return () => core.destroy();
    }, [byo, core]);

    return <HallidayContext.Provider value={ctxValue}>{props.children}</HallidayContext.Provider>;
};
